/* eslint-disable no-undef -- `google` is the Maps JS global, available once the APIProvider has loaded */
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import ListItem from '@mui/material/ListItem'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useMap } from '@vis.gl/react-google-maps'
import React, { memo, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import ServiceTypeAvatar from 'components/common/avatar/ServiceTypeAvatar'
import Link from 'components/common/Link'
import { projectLatLng } from 'components/common/map/projectLatLng'
import WeekDayIcon from 'components/customer-details/content/routing/WeekDayIcon'
import LiquidGlassPaper from 'components/route-manager/RouteManagerMapV2/control-panel/LiquidGlassPaper'
import WorkOrderStatusChip from 'components/work-order/WorkOrderStatusChip'
import WorkOrderStatusAvatar from 'containers/new-route-manager/RouteCard/WorkOrderStatusAvatar'
import { getCustomerDetailsPageUrl } from 'router/routes'

import type { ViewportPoint } from 'components/common/map/projectLatLng'

type DayOfWeek = { day: string; variant: string }

type BulkStop = {
  position?: number | string | null
  accountId?: string | null
  accountName?: string | null
  addressStr?: string | null
  locationName?: string | null
  addressName?: string | null
  serviceName?: string | null
  recurrence?: string | null
  completionStatus?: string | null
  workOrderStatus?: string | null
  intentStatus?: string | null
  workOrderId?: string | null
  workOrderNumber?: string | null
  pricedServiceType?: string | null
  parsedDaysOfWeek?: DayOfWeek[] | null
}

type Props = {
  stop?: BulkStop | null
  position?: { lat: number; lng: number } | null
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>
}

const BulkStopMarkerPopover: React.FC<Props> = ({ stop, position, onMouseEnter, onMouseLeave }) => {
  const map = useMap()
  const theme = useTheme()
  const [screenPos, setScreenPos] = useState<ViewportPoint | null>(null)

  const lat = position?.lat
  const lng = position?.lng

  // Re-project on every camera change so the popover tracks its marker. The
  // popover is portalled to document.body (above the deck canvas + the
  // fullscreen dialog), which is why it can't just be an AdvancedMarker —
  // a floatPane deck canvas paints over markers regardless of their zIndex.
  useEffect(() => {
    if (!map || typeof lat !== 'number' || typeof lng !== 'number') {
      setScreenPos(null)
      return undefined
    }
    const update = () => setScreenPos(projectLatLng(map, lat, lng))
    update()
    const boundsListener = map.addListener('bounds_changed', update)
    return () => {
      google.maps.event.removeListener(boundsListener)
    }
  }, [map, lat, lng])

  if (!stop || !position || !screenPos) return null

  const rawPosition = stop.position
  const sequence = typeof rawPosition === 'number' ? rawPosition + 1 : rawPosition
  const { accountId, accountName, addressStr, serviceName, recurrence, workOrderNumber, pricedServiceType } = stop
  const addressName = stop.locationName || stop.addressName
  const workOrderStatus = stop.completionStatus || stop.workOrderStatus
  const parsedDaysOfWeek = stop.parsedDaysOfWeek ?? []
  const activeDays = parsedDaysOfWeek.filter(d => d.variant === 'active' || d.variant === 'primary')
  const hasDetailSection = serviceName || workOrderNumber || activeDays.length > 0 || recurrence || workOrderStatus

  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        top: screenPos.y,
        left: screenPos.x,
        // Sit centered above the marker point; the popover stays put so the
        // dispatcher can move onto it and click the customer link.
        transform: 'translate(-50%, calc(-100% - 16px))',
        zIndex: theme.zIndex.tooltip + 1,
        pointerEvents: 'auto',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <LiquidGlassPaper blur={18} elevation={8} opacity={0.72} sx={{ maxWidth: 400, maxHeight: '75vh', overflowY: 'auto' }}>
        {/* Account header row — matches StopDetailsTitleSection */}
        <ListItem disableGutters sx={{ px: 2, py: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack alignItems="center" direction="row" spacing={0.5}>
              {sequence != null && (
                <Typography color="text.primary" fontWeight={700} variant="body1">
                  {`#${sequence}`}
                </Typography>
              )}
              {accountName &&
                (accountId ? (
                  <Link
                    color="text.primary"
                    fontWeight={400}
                    rel="noopener noreferrer"
                    sx={{ wordWrap: 'break-word', textDecorationColor: 'inherit' }}
                    target="_blank"
                    to={getCustomerDetailsPageUrl(accountId)}
                    variant="body1"
                  >
                    {accountName}
                  </Link>
                ) : (
                  <Typography color="text.primary" fontWeight={400} sx={{ wordWrap: 'break-word' }} variant="body1">
                    {accountName}
                  </Typography>
                ))}
              <Box flex={1} />
              {workOrderStatus && (
                <WorkOrderStatusAvatar
                  intentStatus={stop.intentStatus}
                  status={workOrderStatus}
                  sx={{ width: 28, height: 28 }}
                  workOrderId={stop.workOrderId}
                />
              )}
            </Stack>
            {(addressStr || addressName) && (
              <Typography color="textSecondary" display="block" lineHeight={1.3} variant="caption">
                {addressStr || addressName}
              </Typography>
            )}
          </Box>
        </ListItem>

        {/* WO summary section — matches StopDetailsTitleSection */}
        {hasDetailSection && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1.5 }}>
              {/* Service + days row */}
              {(serviceName || workOrderNumber || activeDays.length > 0) && (
                <Stack alignItems="center" direction="row" mb={0.75} spacing={1}>
                  <Stack alignItems="center" direction="row" flexShrink={0} spacing={0.5}>
                    {pricedServiceType && <ServiceTypeAvatar serviceType={pricedServiceType} />}
                    {workOrderNumber && (
                      <Typography fontWeight={400} variant="body1">
                        {workOrderNumber}
                      </Typography>
                    )}
                  </Stack>
                  <Stack alignItems="center" direction="row" flex={1} justifyContent="flex-end" minWidth={0} spacing={0.5}>
                    {serviceName && (
                      <Typography noWrap color="text.primary" sx={{ flex: 1, minWidth: 0, textAlign: 'right' }} variant="body1">
                        {serviceName}
                      </Typography>
                    )}
                    {activeDays.map(day => (
                      <WeekDayIcon key={day.day} day={day.day} variant={day.variant} />
                    ))}
                  </Stack>
                </Stack>
              )}

              {/* Chips row */}
              {(workOrderStatus || recurrence) && (
                <Stack alignItems="center" direction="row" flexWrap="wrap" gap={0.75}>
                  {workOrderStatus && (
                    <WorkOrderStatusChip
                      intentStatus={stop.intentStatus ?? undefined}
                      status={workOrderStatus}
                      workOrderId={stop.workOrderId}
                    />
                  )}
                  {recurrence && (
                    <Typography color="text.secondary" variant="caption">
                      {recurrence}
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>
          </>
        )}
      </LiquidGlassPaper>
    </Box>,
    document.body
  )
}

export default memo(BulkStopMarkerPopover)
